import { useState, useCallback } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import SendIcon from '@assets/icons/SendIcon';
import styles from './EmailTagsInput.module.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

export function EmailTagsInput({ label, value, onChange, placeholder }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commit = useCallback(() => {
    const candidate = draft.trim();
    if (!candidate) return;
    if (!EMAIL_RE.test(candidate)) {
      setError(t('K_DS_VALIDATION_RECIPIENT_EMAIL_INVALID', 'Invalid email'));
      return;
    }
    if (value.some((v) => v.toLowerCase() === candidate.toLowerCase())) {
      setDraft('');
      setError(null);
      return;
    }
    onChange([...value, candidate]);
    setDraft('');
    setError(null);
  }, [draft, value, onChange, t]);

  const remove = useCallback(
    (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  return (
    <div className={styles.wrap}>
      <label className={styles.label}>{label}</label>
      <div className={`${styles.inputRow} ${error ? styles.inputRowInvalid : ''}`}>
        <input
          type="email"
          className={styles.input}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          placeholder={placeholder}
          aria-label={label}
        />
        <button
          type="button"
          className={styles.commitBtn}
          onClick={commit}
          disabled={!draft.trim()}
          aria-label={t('K_DS_BUTTON_ADD_EMAIL', 'Add email')}
        >
          <SendIcon />
        </button>
      </div>
      {error ? (
        <span className={styles.error}>{error}</span>
      ) : (
        <span className={styles.helper}>
          {t('K_DS_HELPER_EMAIL_TAGS_INPUT', 'Press Enter or click the send icon to add')}
        </span>
      )}
      {value.length > 0 && (
        <div className={styles.chips}>
          {value.map((email, idx) => (
            <span key={`${email}-${idx}`} className={styles.chip}>
              {email}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => remove(idx)}
                aria-label={t('K_DS_REMOVE', 'Remove')}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
